/*----------------------------------
- DEPENDANCES
----------------------------------*/

// Npm
import React from 'react';
import { useErrorBoundary } from 'preact/hooks';

declare const __DEV__: boolean;

// Core
import useContext from '@/client/context';
import { Link } from '@client/components';

// App
import { SegmentedSwitch } from '@/client/components/ui/Switches';
import { MARKETING_URLS } from '@/client/catalogs/marketing/navigation';

// Resources
import '@/client/assets/tailwind.css';
import {
    Menu, X, Users,
} from 'lucide-preact';

/*----------------------------------
- TYPES
----------------------------------*/

export interface PageProps {
    children: React.ReactNode;
    withLayout?: boolean;
    header?: boolean;
    mainClassName?: string;
}

/*----------------------------------
- COMPOSANT
----------------------------------*/
const Page: React.FC<PageProps> = ({
    children,
    withLayout = true,
    header = true,
    mainClassName = ''
}) => {

    const { Router, app, toast } = useContext();

    const getPathname = React.useCallback(() => {
        if (typeof window !== 'undefined') {
            return window.location.pathname;
        }

        return (Router as any)?.current?.path || '/';
    }, [Router]);

    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
    const [currentPath, setCurrentPath] = React.useState<string>(() => getPathname());
    const [isSaaSSelected, setIsSaaSSelected] = React.useState(() => getPathname() !== '/platform');

    const extractPathname = React.useCallback((payload?: unknown) => {
        if (payload && typeof payload === 'object') {
            const candidate = (payload as { path?: string }).path
                || (payload as { request?: { path?: string } }).request?.path
                || (payload as { context?: { request?: { path?: string } } }).context?.request?.path;

            if (candidate) {
                return candidate;
            }

            const url = (payload as { url?: string }).url
                || (payload as { request?: { url?: string } }).request?.url
                || (payload as { context?: { request?: { url?: string } } }).context?.request?.url;

            if (url && typeof window !== 'undefined') {
                try {
                    return new URL(url, window.location.origin).pathname;
                } catch (error) {
                    console.warn('[page] Unable to parse url from router payload', error);
                }
            }
        }

        return getPathname();
    }, [getPathname]);

    const syncPathState = React.useCallback((payload?: unknown) => {
        const pathname = extractPathname(payload);

        setCurrentPath(prev => (prev === pathname ? prev : pathname));
        setIsSaaSSelected(pathname !== '/platform');
        setMobileMenuOpen(false);
    }, [extractPathname]);

    React.useEffect(() => {
        if (!withLayout) {
            return;
        }

        syncPathState();

        if (typeof (Router as any)?.on === 'function') {
            const detachChange = (Router as any).on('page.change', syncPathState);
            const detachChanged = (Router as any).on('page.changed', syncPathState);
            const detachRendered = (Router as any).on('page.rendered', syncPathState);

            return () => {
                detachChange && detachChange();
                detachChanged && detachChanged();
                detachRendered && detachRendered();
            };
        }

        return undefined;
    }, [Router, syncPathState, withLayout]);

    const handleSegmentedSwitchChange = React.useCallback((value: boolean) => {
        setIsSaaSSelected(value);

        if (typeof window === 'undefined') {
            return;
        }

        const target = value ? '/' : '/platform';
        if (window.location.pathname !== target) {
            window.location.href = target;
        }
    }, []);

    useErrorBoundary(error => {
        app.reportBug({
            before: 'Render page',
            stacktrace: error.stack
        }).then(() => {
            if (__DEV__) {
                console.warn(`The app would be reloaded in production mode.`);
                toast.error(`Errur during page rendering: ${error.message}`);
                console.error(error);
            } else {
                toast.error(`An issue occurred while loading this page. Our dev team will solve it asap.`);
            }
        });
    });

    if (!withLayout) {
        return <>{children}</>;
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-pink-50">
            {/* HEADER */}
            {header && (
            <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-orange-100 z-50 shadow-sm">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <img
                                src="https://sitlmwrkxbvpqzfegvww.supabase.co/storage/v1/object/public/static/logoCP.png"
                                alt="CrossPath Logo"
                                className="w-10 h-10"
                            />
                            <span className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent">CrossPath</span>
                        </div>

                        {/* Desktop Menu */}
                        <nav className="hidden md:flex items-center gap-8">
                            <Link to="/#features"
                                className="text-slate-700 hover:text-orange-600 font-medium transition-colors">Features</Link>
                            <Link to="/#how-it-works"
                                className="text-slate-700 hover:text-orange-600 font-medium transition-colors">How it Works</Link>
                            <Link to="/#about"
                                className="text-slate-700 hover:text-orange-600 font-medium transition-colors">About</Link>
                        </nav>

                        <div className="hidden md:flex items-center gap-3">
                            <SegmentedSwitch
                                value={isSaaSSelected}
                                onChange={handleSegmentedSwitchChange}
                                labels={{ true: 'SaaS', false: 'Platform' }}
                            />
                            <Link to="/login"
                                className="text-slate-700 hover:text-orange-600 font-medium transition-colors px-4 py-2"
                            >
                                Login
                            </Link>
                            <Link to={MARKETING_URLS.saasDemo}
                                className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white px-6 py-2.5 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                            >
                                Request a Demo
                            </Link>
                        </div>

                        {/* Mobile Menu Button */}
                        <button
                            type="button"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="md:hidden p-2"
                        >
                            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>

                    {/* Mobile Menu */}
                    {mobileMenuOpen && (
                        <div className="md:hidden mt-4 pb-4 border-t border-orange-100 pt-4">
                            <nav className="flex flex-col gap-4">
                                <a href="#features" className="text-slate-700 hover:text-orange-600 font-medium">Features</a>
                                <a href="#how-it-works" className="text-slate-700 hover:text-orange-600 font-medium">How it Works</a>
                                <a href="#about" className="text-slate-700 hover:text-orange-600 font-medium">About</a>
                                <div className="pt-2">
                                    <SegmentedSwitch
                                        value={isSaaSSelected}
                                        onChange={handleSegmentedSwitchChange}
                                        labels={{ true: 'SaaS', false: 'Platform' }}
                                        className="w-full justify-between"
                                    />
                                </div>
                                <div className="flex flex-col gap-3 pt-2">
                                    <Link to="/login"
                                        className="text-slate-700 hover:text-orange-600 font-medium text-left"
                                    >
                                        Login
                                    </Link>
                                    <Link to={MARKETING_URLS.saasDemo}
                                        className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-6 py-2.5 rounded-xl font-semibold shadow-lg"
                                    >
                                        Request a Demo
                                    </Link>
                                </div>
                            </nav>
                        </div>
                    )}
                </div>
            </header>
            )}

            <main className={mainClassName}>
                {children}
            </main>

            {/* FOOTER */}
            <footer className="py-16 px-6 lg:px-8 bg-gradient-to-br from-orange-50 to-pink-50">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-pink-500 rounded-xl flex items-center justify-center font-bold text-white shadow-lg">
                                    CP
                                </div>
                                <span className="text-xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent">CrossPath</span>
                            </div>
                            <p className="text-slate-700 text-sm">
                                The intelligent operating system for modern recruiters.
                            </p>
                        </div>

                        <div>
                            <h4 className="font-bold text-slate-900 mb-4">Product</h4>
                            <ul className="space-y-2 text-sm">
                                <li><a href="#features" className="text-slate-600 hover:text-orange-600 transition-colors">Features</a></li>
                                <li><a href="#" className="text-slate-600 hover:text-orange-600 transition-colors">Support</a></li>
                                <li><a href="/privacy" className="text-slate-600 hover:text-orange-600 transition-colors">Privacy Policy</a></li>
                                <li><a href="/terms" className="text-slate-600 hover:text-orange-600 transition-colors">Terms of Service</a></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="font-bold text-slate-900 mb-4">Connect</h4>
                            <Link to="https://www.linkedin.com/company/cross-path-network/?viewAsMember=true" className="inline-flex items-center gap-2 text-slate-600 hover:text-pink-600 transition-colors text-sm">
                                <Users size={18} />
                                LinkedIn
                            </Link>
                        </div>
                    </div>

                    <div className="border-t border-orange-200 pt-8 text-center text-sm text-slate-600">
                        © CrossPath 2025. All rights reserved.
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Page;
